import React, { useState } from "react";
import { convertTokens } from "@date-fns/upgrade/v2";
import { format } from "date-fns";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Unstable_Grid2";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { ChatsCircle } from "@phosphor-icons/react";
import { useHistory, Link as RouterLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import IntakeSpecialistExperience from "components/SelfService/IntakeSpecialistExperience";
import { ResendLoginButton } from "components/ClientDetails/Widgets/TimelineStatistics/TaskButtons";

import { Customer, CustomerSibling } from "api/types/client";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { getAge } from "utils/getAge";
import { formatTimeZoneToAbbreviation } from "utils/formatTimeZoneToAbbreviation";
import { ANTooltip } from "@answersnow/pkg-theme";
import {
  getCustomerVerified,
  getUserPermissionsList,
  getResendingLoginEmail,
  getCustomerUserId,
} from "selectors";
import actions from "actions";

const BlockInfo = ({ label, value }: { label: string; value: string | number }) => {
  return (
    <>
      <Typography component="p" variant="caption" fontWeight={500} color="grey.600">
        {label}
      </Typography>
      <ANTooltip component={Typography} variant="body2" color="text.primary">
        {value}
      </ANTooltip>
    </>
  );
};

/**
 * Displays sibling clients (shared legal guardian). A sibling's name links to
 * their profile only if the current user has permission to view it.
 */
const SiblingInfo = ({ siblings }: { siblings: CustomerSibling[] }) => {
  return (
    <>
      <Typography component="p" variant="caption" fontWeight={500} color="grey.600">
        {siblings.length > 1 ? "Siblings" : "Sibling"}
      </Typography>
      <Typography component="p" variant="body2" color="text.primary">
        {siblings.map((sibling, index) => (
          <React.Fragment key={sibling.id}>
            {index > 0 && ", "}
            {sibling.hasAccess ? (
              <Link component={RouterLink} to={`/clients/${sibling.id}/profile`} underline="hover">
                {sibling.name}
              </Link>
            ) : (
              sibling.name
            )}
          </React.Fragment>
        ))}
      </Typography>
    </>
  );
};

/**
 * Get the status label for the client
 * @param {string} workflowStatus - The workflow status of the client
 * @param {boolean} isActive - Whether the client is active
 * @returns {string} The status label for the client
 */
function getStatusLabel(workflowStatus: string, isActive: boolean): string {
  if (!isActive || !workflowStatus) {
    return "Deactivated";
  }

  switch (workflowStatus) {
    case "SELF_SERVICE_INTAKE":
      return "Intake";

    case "AWAITING_ELIGIBILITY":
    case "AWAITING_ONBOARDING":
      return "Onboarding";

    case "ELIGIBLE":
    case "ASSESSMENT_SCHEDULED":
    case "ASSESSMENT_COMPLETED":
    case "ALLOWED_ASSESSMENT":
    case "ASSESSMENT_COMPLETE":
    case "APPROVE_PLAN":
    case "AWAITING_INSURANCE_APPROVAL":
    case "INSURANCE_APPROVED":
      return "Assessment";

    case "FULL_CUSTOMER":
      return "Therapy";

    case "PAUSED":
      return "Paused";

    default:
      return "Active";
  }
}

export const getStatusColor = (
  status: string
): "primary" | "warning" | "info" | "success" | "error" | "default" | "secondary" => {
  switch (status) {
    case "Intake":
      return "primary";
    case "Onboarding":
      return "warning";
    case "Assessment":
      return "info";
    case "Therapy":
      return "success";
    case "Deactivated":
      return "error";
    default:
      return "default";
  }
};

interface BasicInformationProps {
  customerDetails: { details: Customer; clinician: any };
  customerLoaded?: boolean;
  formatPhoneNumber: (phone: string) => string;
  userInsurance: { is_primary: boolean; is_medicaid: boolean }[];
  workflowStatus?: string;
}

/**
 * This component displays the basic information of the client within a card.
 * Shows skeleton loaders while data is being fetched, then displays actual customer information.
 * @param props - The props for the component.
 * @param {Object} props.customerDetails - The customer details object containing details and clinician info.
 * @param {boolean} [props.customerLoaded=false] - Whether customer data has been loaded. Controls loading skeleton visibility.
 * @param {Function} props.formatPhoneNumber - The function to format the phone number.
 * @param {Object[]} props.userInsurance - The user insurance details array.
 * @returns {React.ReactNode} The Basic Information card component.
 */
const BasicInformation: React.FC<BasicInformationProps> = ({
  customerDetails,
  customerLoaded = false,
  formatPhoneNumber,
  userInsurance,
  workflowStatus,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [intakeOpen, setIntakeOpen] = useState(false);
  const customerInfo = customerDetails?.details;
  const showViewIntake = workflowStatus === "SELF_SERVICE_INTAKE" || !workflowStatus;

  const verified = useSelector(getCustomerVerified);
  const userPermissions = useSelector(getUserPermissionsList);
  const resendingLoginEmail = useSelector(getResendingLoginEmail);
  const customerUserId = useSelector(getCustomerUserId);

  const isActive = customerInfo?.is_active;
  const showResendLogin =
    !verified && isActive && userPermissions?.send_reset_password && !!customerUserId;

  const onResendLoginEmail = () => {
    if (!customerUserId) return;
    dispatch(actions.resendLoginEmail(customerUserId));
  };

  // Show loading skeleton if customer data is not yet loaded
  if (!customerLoaded) {
    return (
      <Grid
        container
        padding={"12px 8px 8px 8px"}
        borderRadius={"16px"}
        gap={0}
        spacing={0}
        bgcolor={"grey.50"}
        data-testid="client-basic-information-loading"
        role="status"
        aria-live="polite"
        aria-label="Loading client basic information"
        sx={{ maxWidth: "1200px", mx: "auto", my: 7 }}
      >
        <Grid xs={12} margin={2}>
          <Skeleton variant="text" width="20%" height={40} />
        </Grid>

        <Grid xs={12}>
          <Box padding={7} borderRadius={3} bgcolor={"background.paper"}>
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  const primaryInsurance = userInsurance?.find((insurance) => insurance.is_primary === true);

  // Get the status label and color for the client
  const statusLabel = getStatusLabel(
    customerInfo?.workflow_status,
    customerInfo?.is_active ?? false
  );
  const statusColor = getStatusColor(statusLabel);

  //Get the designated caregiver for the client
  const designatedCaregiver =
    customerInfo?.first_name && customerInfo?.last_name
      ? `${customerInfo?.first_name} ${customerInfo?.last_name}`
      : "---";

  // Get the primary clinician for the client
  const primaryClinician =
    customerDetails?.clinician?.first_name && customerDetails?.clinician?.last_name
      ? `${customerDetails?.clinician?.first_name} ${customerDetails?.clinician?.last_name}`
      : "---";

  // Get the date of birth for the client
  const dateOfBirth = customerInfo?.dob
    ? format(dateWithoutTimezone(customerInfo?.dob), convertTokens("Do MMMM YYYY")) +
      " - " +
      getAge(dateWithoutTimezone(customerInfo?.dob)) +
      " years old"
    : "----";
  // Get the phone number for the client
  const phoneNumber = customerInfo?.phone_number
    ? formatPhoneNumber(customerInfo?.phone_number)
    : "----";

  // Get the prior authorization for the client
  const priorAuthorization = customerInfo?.pre_auth_required ? "Yes" : "No";

  // Get the last session date for the client
  const lastSessionDate = customerInfo?.last_session_date
    ? format(new Date(customerInfo.last_session_date), convertTokens("Do MMMM YYYY   h:mm a"))
    : "----";

  // Get the time zone for the client
  const timeZone = formatTimeZoneToAbbreviation(customerInfo?.timezone) || "----";

  // Handle opening the messages chat
  const handleOpenMessages = () => {
    history.push(`/messages?chat_id=${customerInfo?.id}`);
  };

  return (
    <Grid
      container
      padding={"16px 8px 8px 8px"}
      borderRadius={"16px"}
      gap={"16px"}
      spacing={0}
      bgcolor={"grey.50"}
      data-testid="client-basic-information"
      sx={{ maxWidth: "1200px", mx: "auto", my: 7 }}
    >
      <Grid
        xs={12}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        paddingX={"8px"}
        data-testid="account-snapshot-header"
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography component="h3" variant="h4" sx={{ fontWeight: 500 }}>
            Account Snapshot
          </Typography>
          <Chip size="small" label={statusLabel} color={statusColor} variant="outlined" />
        </Box>
        <Box display="flex" alignItems="center" gap={3}>
          <Button
            color="secondary"
            size="small"
            startIcon={<ChatsCircle weight="duotone" />}
            onClick={handleOpenMessages}
            aria-label="Open messages conversation"
          >
            Open Messages
          </Button>
          {showResendLogin && (
            <ResendLoginButton
              onResendLoginEmail={onResendLoginEmail}
              resendingLoginEmail={resendingLoginEmail}
            />
          )}
          {showViewIntake && (
            <Button
              color="primary"
              size="small"
              onClick={(event) => {
                event.currentTarget.blur();
                setIntakeOpen(true);
              }}
              aria-label="View intake"
            >
              Open Intake
            </Button>
          )}
        </Box>
      </Grid>

      <Grid xs={12}>
        <Box padding={7} borderRadius={3} bgcolor={"background.paper"}>
          <Grid container rowSpacing={4} gap={0}>
            <Grid xs={12} md={4}>
              <BlockInfo label="Designated caregiver" value={designatedCaregiver} />
            </Grid>

            <Grid xs={12} md={4}>
              <BlockInfo label="Primary clinician" value={primaryClinician} />
            </Grid>

            <Grid xs={12} md={4}>
              <BlockInfo label="Date of birth" value={dateOfBirth} />
            </Grid>

            <Grid xs={12} sm={6} md={4}>
              <BlockInfo label="Phone" value={phoneNumber} />
            </Grid>

            <Grid xs={12} md={4}>
              <BlockInfo label="Last session" value={lastSessionDate} />
            </Grid>

            <Grid xs={12} md={4}>
              <BlockInfo label="Time zone" value={timeZone} />
            </Grid>

            <Grid xs={12} md={4}>
              <BlockInfo label="Email address" value={customerInfo?.email} />
            </Grid>

            <Grid xs={12} md={4}>
              <BlockInfo label="Prior authorization" value={priorAuthorization} />
            </Grid>

            <Grid xs={12} sm={6} md={4}>
              <BlockInfo label="Is Medicaid" value={primaryInsurance?.is_medicaid ? "Yes" : "No"} />
            </Grid>

            {(customerInfo?.siblings?.length ?? 0) > 0 && (
              <Grid xs={12} md={4}>
                <SiblingInfo siblings={customerInfo?.siblings ?? []} />
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>
      {showViewIntake && (
        <IntakeSpecialistExperience open={intakeOpen} onClose={() => setIntakeOpen(false)} />
      )}
    </Grid>
  );
};

export default BasicInformation;
